import type { Lang } from './lib/routes';

type Dict = Record<string, string>;

// Game logic is language-neutral (enum tokens); only the display layer translates.
// Missing keys fall back to English, so partial translations degrade gracefully.

const en: Dict = {
  'lang.name': 'English',
  'sub.daily': 'DAILY · GUESS THE ANIME',
  'kicker': "TODAY'S ANIME",
  'hero.title': 'Animedle — Guess Today’s Anime',
  'hero.title.character': 'Animedle Character — Guess Today’s Anime Character',
  'hero.title.pixel': 'Animedle Pixel — Guess the Anime',
  'hero.sub': 'Animedle narrows the anime down by attributes — studio, year, genres and more. Unlimited guesses. Type any title to begin.',
  'hero.sub.pixel': 'In Animedle Pixel the cover sharpens with every guess. Unlimited guesses — type any anime title to begin.',
  'hero.sub.character': 'Animedle Character: guess today’s anime character from attribute clues — series, gender, debut year and more.',
  'tries.remaining': '{n} / {max} left',
  'tries.count': '{n} guesses',
  'tries.done': 'Done',
  'input.placeholder': 'Type a title… (e.g. Frieren, One Piece)',
  'input.placeholder.char': 'Type a character… (e.g. Levi, Gojo)',
  'guess': 'GUESS',
  'empty.first': 'Type any anime title to make your first guess.',
  'empty.first.char': 'Type any anime character to make your first guess.',
  'empty.yesterday': 'Yesterday: {title}',
  'yesterday.label': "YESTERDAY'S ANIME",
  'today.label': 'Today',
  'today.progress': '{n}/{m} modes solved today',
  'hint.show': '💡 Need a hint?',
  'hint.label': 'FREE HINT',
  'hint.text': 'Today’s anime is a {decade}s {genre} title.',
  'hint.text.char': 'Today’s character is {gender} and appears in {anime}.',
  'mode.classic': 'Classic',
  'mode.emoji': 'Emoji',
  'mode.pixel': 'Pixel',
  'mode.character': 'Character',
  'mode.endless': 'Endless',
  'mode.soon': 'Coming soon',
  'mode.unavailable': 'Temporarily unavailable',
  'mode.unavailable.body': 'This Animedle mode is temporarily unavailable. Try another mode.',
  'takedown.title': 'Animedle is temporarily unavailable',
  'takedown.body': 'Animedle is temporarily offline. Please check back soon.',
  'dmca.line': 'Rights holders may request content removal:',
  'victory.won': 'SOLVED',
  'victory.lost': 'MISSED',
  'victory.solvedIn': 'Solved in {n} guesses',
  'victory.tryTomorrow': 'Better luck tomorrow',
  'streak': 'STREAK',
  'next': 'Next in',
  'share': 'SHARE',
  'share.image': 'Save image',
  'share.copyResult': 'Copy result',
  'share.copyLink': 'Copy link',
  'toast.shared': 'Shared!',
  'toast.copied': 'Copied!',
  'legend.match': 'Match',
  'legend.close': 'Close',
  'legend.no': 'No',
  'legend.dir': '▲ higher / ▼ lower',
  'col.anime': 'Anime', 'col.year': 'Year', 'col.season': 'Season',
  'col.eps': 'Eps', 'col.studio': 'Studio', 'col.genres': 'Genres', 'col.score': 'Score',
  'col.character': 'Character', 'col.gender': 'Gender', 'col.age': 'Age', 'col.fav': 'Favourites',
  'season.WINTER': 'Winter', 'season.SPRING': 'Spring', 'season.SUMMER': 'Summer', 'season.FALL': 'Fall',
  'disclaimer.1': 'Animedle is an unofficial, fan-made game, not affiliated with or endorsed by any studio or publisher.',
  'disclaimer.2': 'All anime titles, characters & images are property of their respective owners. Data by',
  'seo.keywords': 'animedle, animedle game, anime dle, anime wordle, daily anime game, guess the anime, anime guessing game, anime quiz, animedle character, animedle pixel',
  'seo.title': 'Animedle — Daily Anime Guessing Game',
  'seo.desc': 'Animedle is a free daily anime guessing game. Guess today’s anime from attribute clues — studio, year, genres and more. A new Animedle puzzle every day.',
  'seo.title.pixel': 'Animedle Pixel — Guess the Anime from a Pixelated Cover',
  'seo.desc.pixel': 'Play Animedle Pixel: guess the anime from a pixelated cover that sharpens with every guess. A new Animedle puzzle every day.',
  'seo.title.character': 'Animedle Character — Guess the Anime Character',
  'seo.desc.character': 'Play Animedle Character: guess today’s anime character from clues like series, gender, debut year and popularity. A new Animedle character every day.',
  'seo.title.emoji': 'Animedle Emoji — Guess the Anime from Emoji',
  'seo.desc.emoji': 'Play Animedle Emoji: guess today’s anime from a set of emoji clues. A new Animedle puzzle every day.',
  // ---- visible SEO content area (localized) ----
  'seo.about.h2': 'About Animedle',
  'seo.about.p': 'Animedle is a free daily anime guessing game. Every day, Animedle picks a new anime to figure out from attribute clues — the studio, release year, season, episode count, genres and score. Like Wordle for anime, Animedle gives everyone worldwide the same daily answer, unlimited guesses, and a streak to keep alive. No sign-up, no download — just open Animedle and play.',
  'seo.howto.h2': 'How to play Animedle',
  'seo.howto.1': 'Type any anime title and submit your guess.',
  'seo.howto.2': 'Each attribute turns green if it matches, yellow if it’s close, and red if it’s wrong. Arrows (▲/▼) tell you whether the answer is higher or lower.',
  'seo.howto.3': 'Use the clues to narrow it down — Animedle gives you unlimited guesses to solve the anime.',
  'seo.howto.4': 'Come back tomorrow for a new daily Animedle and extend your streak.',
  'seo.modes.h2': 'Animedle game modes',
  'seo.modes.intro': 'Play a different Animedle puzzle every day across multiple modes:',
  'seo.modes.classic': 'guess the anime from its attributes.',
  'seo.modes.character': 'guess the anime character from series, gender and more.',
  'seo.modes.pixel': 'a pixelated cover that sharpens with every guess.',
  'seo.faq.h2': 'Animedle FAQ',
  'seo.faq.q1': 'Is Animedle free?',
  'seo.faq.a1': 'Yes. Animedle is completely free to play, with no account required.',
  'seo.faq.q2': 'When does the daily Animedle reset?',
  'seo.faq.a2': 'Animedle resets every day at 00:00 UTC. Everyone around the world gets the same daily anime.',
  'seo.faq.q3': 'Where does Animedle get its data?',
  'seo.faq.a3': 'Animedle sources anime titles, studios, genres and cover art from AniList.',
  'seo.faq.q4': 'How is the Animedle answer chosen?',
  'seo.faq.a4': 'Each day Animedle deterministically selects a popular anime, so every player worldwide solves the same puzzle.',
};

const es: Dict = {
  'lang.name': 'Español',
  'sub.daily': 'DIARIO · ADIVINA EL ANIME',
  'kicker': 'ANIME DE HOY',
  'hero.title': 'Animedle — Adivina el Anime de Hoy',
  'hero.title.character': 'Animedle Character — Adivina el Personaje de Anime',
  'hero.title.pixel': 'Animedle Pixel — Adivina el Anime',
  'hero.sub': 'Animedle acota el anime por atributos — estudio, año, géneros y más. Intentos ilimitados. Escribe cualquier título para empezar.',
  'hero.sub.pixel': 'En Animedle Pixel la portada se aclara con cada intento. Intentos ilimitados — escribe cualquier título para empezar.',
  'hero.sub.character': 'Animedle Character: adivina el personaje de anime de hoy por pistas — serie, género, año de debut y más.',
  'tries.remaining': '{n} / {max} restantes',
  'tries.count': '{n} intentos',
  'tries.done': 'Listo',
  'input.placeholder': 'Escribe un título… (p. ej. Frieren, One Piece)',
  'guess': 'ADIVINAR',
  'empty.first': 'Escribe cualquier título de anime para tu primer intento.',
  'empty.yesterday': 'Ayer: {title}',
  'mode.classic': 'Clásico',
  'mode.character': 'Personaje',
  'mode.soon': 'Próximamente',
  'mode.unavailable': 'No disponible temporalmente',
  'mode.unavailable.body': 'Este modo de Animedle no está disponible temporalmente. Prueba otro modo.',
  'takedown.title': 'Animedle no está disponible temporalmente',
  'takedown.body': 'Animedle está temporalmente fuera de línea. Vuelve pronto.',
  'dmca.line': 'Los titulares de derechos pueden solicitar la retirada de contenido:',
  'victory.won': 'RESUELTO',
  'victory.lost': 'FALLASTE',
  'victory.solvedIn': 'Resuelto en {n} intentos',
  'victory.tryTomorrow': 'Suerte mañana',
  'streak': 'RACHA',
  'next': 'Siguiente en',
  'share': 'COMPARTIR',
  'toast.shared': '¡Compartido!',
  'yesterday.label': 'ANIME DE AYER',
  'today.label': 'Hoy',
  'today.progress': '{n}/{m} modos resueltos hoy',
  'hint.show': '💡 ¿Una pista?',
  'hint.label': 'PISTA GRATIS',
  'hint.text': 'El anime de hoy es un título de {genre} de los {decade}.',
  'share.image': 'Guardar imagen',
  'share.copyResult': 'Copiar resultado',
  'share.copyLink': 'Copiar enlace',
  'toast.copied': '¡Copiado!',
  'legend.match': 'Igual', 'legend.close': 'Cerca', 'legend.no': 'No',
  'col.year': 'Año', 'col.season': 'Temporada', 'col.eps': 'Eps', 'col.studio': 'Estudio', 'col.genres': 'Géneros', 'col.score': 'Nota',
  'season.WINTER': 'Invierno', 'season.SPRING': 'Primavera', 'season.SUMMER': 'Verano', 'season.FALL': 'Otoño',
  'disclaimer.1': 'Animedle es un juego no oficial hecho por fans, sin afiliación ni respaldo de ningún estudio o editorial.',
  'disclaimer.2': 'Todos los títulos, personajes e imágenes pertenecen a sus respectivos dueños. Datos de',
  'seo.title': 'Animedle — Juego diario de adivinar anime',
  'seo.keywords': 'animedle, animedle juego, anime dle, anime wordle, juego diario de anime, adivina el anime, juego de adivinar anime, quiz de anime',
  'seo.desc': 'Animedle es un juego diario y gratis de adivinar anime. Adivina el anime de hoy con pistas de atributos — estudio, año, géneros y más. Un nuevo Animedle cada día.',
  'seo.desc.pixel': 'Juega a Animedle Pixel: adivina el anime desde una portada pixelada que se aclara con cada intento. Un nuevo Animedle cada día.',
  'seo.desc.character': 'Juega a Animedle Character: adivina el personaje de anime de hoy por pistas como serie, género, año de debut y popularidad. Un nuevo personaje cada día.',
  'seo.about.h2': 'Acerca de Animedle',
  'seo.about.p': 'Animedle es un juego diario y gratuito de adivinar anime. Cada día, Animedle elige un nuevo anime para descubrir a partir de pistas de atributos — el estudio, el año de estreno, la temporada, el número de episodios, los géneros y la nota. Como un Wordle de anime, Animedle da a todo el mundo la misma respuesta diaria, intentos ilimitados y una racha que mantener. Sin registro ni descargas — solo abre Animedle y juega.',
  'seo.howto.h2': 'Cómo jugar a Animedle',
  'seo.howto.1': 'Escribe cualquier título de anime y envía tu intento.',
  'seo.howto.2': 'Cada atributo se pone verde si coincide, amarillo si está cerca y rojo si no. Las flechas (▲/▼) indican si la respuesta es mayor o menor.',
  'seo.howto.3': 'Usa las pistas para acotar — Animedle te da intentos ilimitados para resolver el anime.',
  'seo.howto.4': 'Vuelve mañana por un nuevo Animedle diario y amplía tu racha.',
  'seo.modes.h2': 'Modos de juego de Animedle',
  'seo.modes.intro': 'Juega un Animedle diferente cada día en varios modos:',
  'seo.modes.classic': 'adivina el anime por sus atributos.',
  'seo.modes.character': 'adivina el personaje de anime por serie, género y más.',
  'seo.modes.pixel': 'una portada pixelada que se aclara con cada intento.',
  'seo.faq.h2': 'Preguntas frecuentes de Animedle',
  'seo.faq.q1': '¿Animedle es gratis?',
  'seo.faq.a1': 'Sí. Animedle es totalmente gratis y no requiere cuenta.',
  'seo.faq.q2': '¿Cuándo se reinicia el Animedle diario?',
  'seo.faq.a2': 'Animedle se reinicia cada día a las 00:00 UTC. Todo el mundo recibe el mismo anime diario.',
  'seo.faq.q3': '¿De dónde saca Animedle sus datos?',
  'seo.faq.a3': 'Animedle obtiene títulos, estudios, géneros y portadas de AniList.',
  'seo.faq.q4': '¿Cómo se elige la respuesta de Animedle?',
  'seo.faq.a4': 'Cada día Animedle selecciona de forma determinista un anime popular, así todos resuelven el mismo puzzle.',
};

const pt: Dict = {
  'lang.name': 'Português',
  'sub.daily': 'DIÁRIO · ADIVINHE O ANIME',
  'kicker': 'ANIME DE HOJE',
  'hero.title': 'Animedle — Adivinhe o Anime de Hoje',
  'hero.title.character': 'Animedle Character — Adivinhe o Personagem de Anime',
  'hero.title.pixel': 'Animedle Pixel — Adivinhe o Anime',
  'hero.sub': 'O Animedle filtra o anime por atributos — estúdio, ano, gêneros e mais. Tentativas ilimitadas. Digite qualquer título para começar.',
  'hero.sub.pixel': 'No Animedle Pixel a capa fica mais nítida a cada palpite. Tentativas ilimitadas — digite qualquer título para começar.',
  'hero.sub.character': 'Animedle Character: adivinhe o personagem de anime de hoje por pistas — série, gênero, ano de estreia e mais.',
  'tries.remaining': '{n} / {max} restantes',
  'tries.count': '{n} tentativas',
  'tries.done': 'Pronto',
  'input.placeholder': 'Digite um título… (ex.: Frieren, One Piece)',
  'guess': 'ADIVINHAR',
  'empty.first': 'Digite qualquer título de anime para o primeiro palpite.',
  'empty.yesterday': 'Ontem: {title}',
  'mode.classic': 'Clássico',
  'mode.character': 'Personagem',
  'mode.soon': 'Em breve',
  'mode.unavailable': 'Indisponível temporariamente',
  'mode.unavailable.body': 'Este modo do Animedle está indisponível temporariamente. Tente outro modo.',
  'takedown.title': 'Animedle está indisponível temporariamente',
  'takedown.body': 'O Animedle está temporariamente offline. Volte em breve.',
  'dmca.line': 'Os detentores de direitos podem solicitar a remoção de conteúdo:',
  'victory.won': 'ACERTOU',
  'victory.lost': 'ERROU',
  'victory.solvedIn': 'Resolvido em {n} tentativas',
  'victory.tryTomorrow': 'Mais sorte amanhã',
  'streak': 'SEQUÊNCIA',
  'next': 'Próximo em',
  'share': 'COMPARTILHAR',
  'toast.shared': 'Compartilhado!',
  'yesterday.label': 'ANIME DE ONTEM',
  'today.label': 'Hoje',
  'today.progress': '{n}/{m} modos resolvidos hoje',
  'hint.show': '💡 Uma dica?',
  'hint.label': 'DICA GRÁTIS',
  'hint.text': 'O anime de hoje é um título de {genre} dos anos {decade}.',
  'share.image': 'Salvar imagem',
  'share.copyResult': 'Copiar resultado',
  'share.copyLink': 'Copiar link',
  'toast.copied': 'Copiado!',
  'legend.match': 'Igual', 'legend.close': 'Perto', 'legend.no': 'Não',
  'col.year': 'Ano', 'col.season': 'Temporada', 'col.eps': 'Eps', 'col.studio': 'Estúdio', 'col.genres': 'Gêneros', 'col.score': 'Nota',
  'season.WINTER': 'Inverno', 'season.SPRING': 'Primavera', 'season.SUMMER': 'Verão', 'season.FALL': 'Outono',
  'disclaimer.1': 'Animedle é um jogo não oficial feito por fãs, sem afiliação ou endosso de qualquer estúdio ou editora.',
  'disclaimer.2': 'Todos os títulos, personagens e imagens pertencem aos seus respectivos donos. Dados de',
  'seo.title': 'Animedle — Jogo diário de adivinhar anime',
  'seo.keywords': 'animedle, animedle jogo, anime dle, anime wordle, jogo diário de anime, adivinhe o anime, jogo de adivinhar anime, quiz de anime',
  'seo.desc': 'Animedle é um jogo diário e gratuito de adivinhar anime. Adivinhe o anime de hoje com pistas de atributos — estúdio, ano, gêneros e mais. Um novo Animedle todo dia.',
  'seo.desc.pixel': 'Jogue Animedle Pixel: adivinhe o anime por uma capa pixelada que fica mais nítida a cada palpite. Um novo Animedle todo dia.',
  'seo.desc.character': 'Jogue Animedle Character: adivinhe o personagem de anime de hoje por pistas como série, gênero, ano de estreia e popularidade. Um novo personagem todo dia.',
  'seo.about.h2': 'Sobre o Animedle',
  'seo.about.p': 'Animedle é um jogo diário e gratuito de adivinhar anime. Todo dia, o Animedle escolhe um novo anime para descobrir a partir de pistas de atributos — o estúdio, o ano de estreia, a temporada, o número de episódios, os gêneros e a nota. Como um Wordle de anime, o Animedle dá a todos a mesma resposta diária, tentativas ilimitadas e uma sequência para manter. Sem cadastro nem download — é só abrir o Animedle e jogar.',
  'seo.howto.h2': 'Como jogar Animedle',
  'seo.howto.1': 'Digite qualquer título de anime e envie seu palpite.',
  'seo.howto.2': 'Cada atributo fica verde se corresponde, amarelo se está perto e vermelho se está errado. As setas (▲/▼) indicam se a resposta é maior ou menor.',
  'seo.howto.3': 'Use as pistas para filtrar — o Animedle dá tentativas ilimitadas para resolver o anime.',
  'seo.howto.4': 'Volte amanhã para um novo Animedle diário e aumente sua sequência.',
  'seo.modes.h2': 'Modos de jogo do Animedle',
  'seo.modes.intro': 'Jogue um Animedle diferente todo dia em vários modos:',
  'seo.modes.classic': 'adivinhe o anime pelos seus atributos.',
  'seo.modes.character': 'adivinhe o personagem de anime por série, gênero e mais.',
  'seo.modes.pixel': 'uma capa pixelada que fica mais nítida a cada palpite.',
  'seo.faq.h2': 'Perguntas frequentes do Animedle',
  'seo.faq.q1': 'O Animedle é gratuito?',
  'seo.faq.a1': 'Sim. O Animedle é totalmente gratuito e não exige conta.',
  'seo.faq.q2': 'Quando o Animedle diário reinicia?',
  'seo.faq.a2': 'O Animedle reinicia todo dia às 00:00 UTC. Todos recebem o mesmo anime diário.',
  'seo.faq.q3': 'De onde o Animedle tira os dados?',
  'seo.faq.a3': 'O Animedle obtém títulos, estúdios, gêneros e capas do AniList.',
  'seo.faq.q4': 'Como a resposta do Animedle é escolhida?',
  'seo.faq.a4': 'Todo dia o Animedle seleciona de forma determinística um anime popular, então todos resolvem o mesmo desafio.',
};

const ja: Dict = {
  'lang.name': '日本語',
  'sub.daily': 'デイリー · アニメ当て',
  'kicker': '今日のアニメ',
  'hero.title': 'Animedle — 今日のアニメを当てよう',
  'hero.title.character': 'Animedle キャラ — 今日のアニメキャラを当てよう',
  'hero.title.pixel': 'Animedle ピクセル — アニメを当てよう',
  'hero.sub': 'Animedle は属性でアニメを絞り込むゲーム — スタジオ・年代・ジャンルなど。回数無制限。タイトルを入力して開始。',
  'hero.sub.pixel': 'Animedle ピクセルでは予想ごとに表紙が鮮明に。回数無制限 — タイトルを入力して開始。',
  'hero.sub.character': 'Animedle キャラ：作品・性別・登場年などのヒントで今日のアニメキャラを当てよう。',
  'tries.remaining': '残り {n} / {max}',
  'tries.count': '{n} 回',
  'tries.done': '完了',
  'input.placeholder': 'タイトルを入力…（例：葬送のフリーレン）',
  'guess': '送信',
  'empty.first': 'アニメのタイトルを入力して最初の予想を。',
  'empty.yesterday': '昨日：{title}',
  'mode.classic': 'クラシック',
  'mode.character': 'キャラ',
  'mode.soon': '近日公開',
  'mode.unavailable': '一時的に利用できません',
  'mode.unavailable.body': 'この Animedle モードは一時的に利用できません。別のモードをお試しください。',
  'takedown.title': 'Animedle は一時的に利用できません',
  'takedown.body': 'Animedle は一時的にオフラインです。しばらくしてからご確認ください。',
  'dmca.line': '権利者の方はコンテンツ削除を申請できます：',
  'victory.won': 'クリア',
  'victory.lost': '失敗',
  'victory.solvedIn': '{n} 回でクリア',
  'victory.tryTomorrow': 'また明日',
  'streak': '連勝',
  'next': '次まで',
  'share': 'シェア',
  'toast.shared': 'シェアしました！',
  'yesterday.label': '昨日のアニメ',
  'today.label': '今日',
  'today.progress': '本日 {n}/{m} モードクリア',
  'hint.show': '💡 ヒントを見る',
  'hint.label': '無料ヒント',
  'hint.text': '今日のアニメは {decade} 年代の {genre} 作品。',
  'share.image': '画像を保存',
  'share.copyResult': '結果をコピー',
  'share.copyLink': 'リンクをコピー',
  'toast.copied': 'コピーしました！',
  'legend.match': '一致', 'legend.close': '近い', 'legend.no': '不一致',
  'col.year': '年', 'col.season': '季', 'col.eps': '話数', 'col.studio': '制作', 'col.genres': 'ジャンル', 'col.score': '評価',
  'season.WINTER': '冬', 'season.SPRING': '春', 'season.SUMMER': '夏', 'season.FALL': '秋',
  'disclaimer.1': 'Animedle は非公式のファンメイドゲームであり、いかなるスタジオ・出版社とも提携・承認関係はありません。',
  'disclaimer.2': 'すべてのタイトル・キャラクター・画像は各権利者に帰属します。データ提供：',
  'seo.title': 'Animedle — 毎日のアニメ当てゲーム',
  'seo.keywords': 'animedle, アニメ当て, anime dle, anime wordle, 毎日アニメゲーム, アニメ クイズ, アニメ 当てゲーム',
  'seo.desc': 'Animedle は無料の毎日アニメ当てゲーム。スタジオ・年代・ジャンルなどの属性ヒントから今日のアニメを当てよう。毎日新しい Animedle。',
  'seo.desc.pixel': 'Animedle ピクセル：予想ごとに鮮明になるピクセル表紙からアニメを当てよう。毎日新しい Animedle。',
  'seo.desc.character': 'Animedle キャラ：作品・性別・登場年・人気度などのヒントで今日のアニメキャラを当てよう。毎日新しいキャラ。',
  'seo.about.h2': 'Animedle について',
  'seo.about.p': 'Animedle は無料の毎日アニメ当てゲームです。Animedle は毎日新しいアニメを選び、スタジオ・放送年・季節・話数・ジャンル・評価などの属性ヒントから当てます。アニメ版 Wordle のように、Animedle は世界中で同じ答え・無制限の予想・連勝記録を提供します。登録もダウンロードも不要 — Animedle を開いてすぐ遊べます。',
  'seo.howto.h2': 'Animedle の遊び方',
  'seo.howto.1': 'アニメのタイトルを入力して予想を送信。',
  'seo.howto.2': '各属性は一致で緑、近いと黄、外れで赤。矢印（▲/▼）は答えがより上か下かを示します。',
  'seo.howto.3': 'ヒントで絞り込もう — Animedle は予想回数無制限。',
  'seo.howto.4': '明日また新しい Animedle に挑戦して連勝を伸ばそう。',
  'seo.modes.h2': 'Animedle のゲームモード',
  'seo.modes.intro': '毎日いろいろなモードで Animedle を楽しもう：',
  'seo.modes.classic': '属性からアニメを当てる。',
  'seo.modes.character': '作品・性別などからアニメキャラを当てる。',
  'seo.modes.pixel': '予想ごとに鮮明になるピクセル表紙。',
  'seo.faq.h2': 'Animedle のよくある質問',
  'seo.faq.q1': 'Animedle は無料ですか？',
  'seo.faq.a1': 'はい。Animedle は完全無料で、アカウント登録も不要です。',
  'seo.faq.q2': '毎日の Animedle はいつリセットされますか？',
  'seo.faq.a2': 'Animedle は毎日 00:00 UTC にリセットされ、世界中で同じアニメになります。',
  'seo.faq.q3': 'Animedle のデータはどこから？',
  'seo.faq.a3': 'Animedle はタイトル・スタジオ・ジャンル・表紙を AniList から取得しています。',
  'seo.faq.q4': 'Animedle の答えはどう決まりますか？',
  'seo.faq.a4': 'Animedle は毎日、人気アニメを決定的に選ぶため、世界中の全員が同じ問題を解きます。',
};

const DICTS: Record<Lang, Dict> = { en, es, pt, ja };

export function translate(lang: Lang, key: string, vars?: Record<string, string | number>): string {
  let s = DICTS[lang][key] ?? en[key] ?? key;
  if (vars) for (const [k, v] of Object.entries(vars)) s = s.replace(`{${k}}`, String(v));
  return s;
}
